import React, { KeyboardEvent, FocusEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import Style from "./TagListInput2.module.css";

type TagItem = string | { id: string, value: string };
export type TagItemChange = {
    action: "add",
    value: string
} | {
    action: "remove",
    value: string,
    index: number
};

type TagAddHandler = (item: string) => void;
type TagRemoveHandler = (item: TagItem, index: number) => void;

interface ITagList2Props {
    className?: string;
    style?: React.CSSProperties;
    name?: string;
    id?: string;
    mode?: "inline" | "stacked";
    disabled?: boolean;

    addOnBlur?: boolean;
    items: TagItem[];

    onRemoveItem?: TagRemoveHandler;
    onAddItem?: TagAddHandler;
    onItemsChange?: (change: TagItemChange, name: string | undefined) => void;

    onRenderSuggestionPanel?: (searchText: string, addHandler: TagAddHandler, removeHandler: TagRemoveHandler) => ReactNode;
    onRequestSuggestions?: (searchText: string) => TagItem[];
}

interface ITagListItemProps {
    disabled: boolean;
    onRemoveClick: () => void;
}

const TagListItem: React.FC<ITagListItemProps> = function (props) {
    return <div className={Style.tagListItem}><span>{props.children}</span>{!props.disabled ? <button type="button" className="icon-times" title="Remove item" onClick={props.onRemoveClick} /> : null}</div>;
}

const TagListInput2: React.FC<ITagList2Props> = function (props) {
    const [searchText, setSearchText] = useState<string>("");
    const [suggestions, setSuggestions] = useState<TagItem[]>([]);
    const searchBoxRef = useRef<HTMLInputElement | null>(null);

    const { onRequestSuggestions, onRenderSuggestionPanel, onAddItem, onRemoveItem, onItemsChange } = props;
    let { className, disabled } = props;
    const itemCount = props.items?.length || 0;

    if (props.mode === "stacked") {
        className += " " + Style.stacked;
    }

    if (!!disabled) {
        className += " " + Style.disabled;
    }

    const updateSearchText = useCallback((searchText: string) => {
        setSearchText(searchText);

        if (onRequestSuggestions) {
            const suggestions = onRequestSuggestions(searchText);

            setSuggestions(suggestions);
        }
    }, [onRequestSuggestions]);

    const addItemAndClear = useCallback((text: string) => {
        if (disabled) {
            return;
        }

        onAddItem?.(text);
        onItemsChange?.({ action: "add", value: text }, props.name);

        updateSearchText("");
    }, [onAddItem, disabled]);

    const removeItem = useCallback((text: TagItem, index: number) => {
        if (disabled) {
            return;
        }

        const deleteValue = typeof text === "string" ? text : text.id;
        onRemoveItem?.(deleteValue, index);
        onItemsChange?.({ action: "remove", value: deleteValue, index: index }, props.name);

        // Only focus the search box if we delete the last item
        if (index === itemCount - 1) {
            searchBoxRef.current?.focus();
        }
    }, [onRemoveItem, searchBoxRef, itemCount, disabled]);

    const handleKeydown = (e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case "Enter":
                e.preventDefault();
                addItemAndClear(searchText);
                break;
            case "Escape":
                e.preventDefault();
                updateSearchText("");
                break;
            case "ArrowDown":
                e.preventDefault();
                (e.currentTarget.nextElementSibling as HTMLElement)?.focus();
                break;
        }
    };

    const handleSuggestionKeydown = (e: KeyboardEvent<HTMLElement>, value: string) => {
        console.log(e.key, e.currentTarget.nextElementSibling);
        switch (e.key) {
            case "Enter":
                e.preventDefault();
                addItemAndClear(value);
                searchBoxRef.current?.focus();
                break;
            case "ArrowDown":
                e.preventDefault();
                (e.currentTarget.nextElementSibling as HTMLElement)?.focus();
                break;
            case "ArrowUp":
                e.preventDefault();
                (e.currentTarget.previousElementSibling as HTMLElement)?.focus();
                break;
        }
    };

    const handleBlur = (e: FocusEvent) => {
        const blurHandlerElement = e.currentTarget;
        if (props.addOnBlur === true && (!e.relatedTarget || !blurHandlerElement.contains(e.relatedTarget as Node))) {
            // Add item if focus has left the element completely
            if (searchText) {
                addItemAndClear(searchText);
            }
        }
    }

    const handleSuggestionBoxFocus = (e: FocusEvent) => {
        if (e.currentTarget !== e.target) {
            return;
        }

        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as Node)) {
            // Focus came from a child; focus search box
            searchBoxRef.current?.focus();
        }
        else {
            const firstFocusable = e.currentTarget.querySelector("input, select, textarea, [tabindex]");
            (firstFocusable as HTMLElement)?.focus();
        }
    }

    useEffect(() => {
        if (onRequestSuggestions) {
            const suggestions = onRequestSuggestions(searchText);

            setSuggestions(suggestions || []);
        }
    }, [onRequestSuggestions]);

    return <div className={`${Style.tagListInput} ${className || ""}`} style={props.style} id={props.id} onBlur={handleBlur}>
        {Array.isArray(props.items) && props.items?.map((a, index) => <TagListItem key={typeof a === "string" ? index : a.id} disabled={!!disabled} onRemoveClick={() => removeItem(a, index)}>{typeof a === "string" ? a : a.value}</TagListItem>)}
        {!disabled ? <input ref={searchBoxRef} type="text" className={Style.tagListSearchInput} placeholder="Search..." value={searchText} onChange={e => updateSearchText(e.currentTarget.value)} onKeyDown={handleKeydown} /> : null}
        <div className={Style.suggestionPanel} key={searchText} tabIndex={(!!onRenderSuggestionPanel || suggestions.length > 0) ? 0 : -1} onFocus={handleSuggestionBoxFocus}>
            {onRenderSuggestionPanel
                ? onRenderSuggestionPanel(searchText, addItemAndClear, removeItem)
                : suggestions.length === 0
                    ? <span><i>No suggestions</i></span>
                    : <ul className={Style.list}>
                        {suggestions.map(a => <li key={typeof a === "string" ? a : a.id} tabIndex={-1} onClick={() => addItemAndClear(typeof a === "string" ? a : a.value)} onKeyDown={e => handleSuggestionKeydown(e, typeof a === "string" ? a : a.value)}>{typeof a === "string" ? a : a.value}</li>)}
                    </ul>}
        </div>
    </div>;
}

export default TagListInput2;
